import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import {
  selectAvailablePlansByProduct,
  selectBillingEstimateForPlanChange,
  selectCurrentBillingCycle,
  selectCurrentPlanByProduct,
  selectFuturePhase,
  selectPendingDowngrades,
} from 'ducks/billing/selectors'
import { doFetchBillingData } from 'ducks/billing/operations'
import { isPlanLimitsExceeded } from 'ducks/billing/util'
import { strftime } from 'util/date'
import { selectUsageForProduct } from 'ducks/billing/selectors/usage'
import { AdminAccessDrawer } from '../../NoAccess'
import { styles } from '../styles'
import ContactCard from '../ContactCard'
import Features from '../Features'

const BillingDownGradePlanConfirmation = ({
  open,
  onExit,
  onClose,
  drawerResourceId,
  drawerProductType,
  drawerOtherProductPlanId,
  drawerOtherProductProductType,
}) => {
  const dispatch = useDispatch()

  const billingPlans = useSelector(state =>
    selectAvailablePlansByProduct(state, drawerProductType)
  )
  const currentCycle = useSelector(selectCurrentBillingCycle)
  const pendingDowngrades = useSelector(selectPendingDowngrades)
  const pendingDowngrade = pendingDowngrades[drawerProductType]
  const { cycle } = pendingDowngrade || {}
  const newPlan = billingPlans.find(plan => plan.planId === drawerResourceId)
  const otherProductPlan = billingPlans.find(
    plan => plan.planId === drawerOtherProductPlanId
  )
  const { nextBillDate } = useSelector(selectBillingEstimateForPlanChange)
  const { startDate } = useSelector(selectFuturePhase) || {}
  const scheduledDate = startDate || nextBillDate || new Date().getTime() / 1000
  const currentPlan = useSelector(state =>
    selectCurrentPlanByProduct(state, drawerProductType)
  )?.plan
  const usage = useSelector(state =>
    selectUsageForProduct(state, drawerProductType)
  )
  const otherProductUsage = useSelector(state =>
    selectUsageForProduct(state, drawerOtherProductProductType)
  )
  const isDowngradeCycle = cycle && currentCycle !== cycle
  const isProductPlanLimitsExceeded =
    isDowngradeCycle || !newPlan ? false : isPlanLimitsExceeded(newPlan, usage)
  const isOtherProductPlanLimitsExceeded =
    isDowngradeCycle || !otherProductPlan
      ? false
      : isPlanLimitsExceeded(otherProductPlan, otherProductUsage)
  const planLimitsExceeded =
    isProductPlanLimitsExceeded || isOtherProductPlanLimitsExceeded

  const handleClose = useCallback(
    () => {
      onClose({
        ignoreStack: true,
      })
    },
    [onClose]
  )

  const footer = (
    <ModalBtns
      css={styles.btns}
      tertiaryBtnText="Close"
      onClickTertiaryBtn={handleClose}
    />
  )

  useEffect(
    () => {
      dispatch(doFetchBillingData())
    },
    [dispatch]
  )

  return (
    <AdminAccessDrawer
      title="Downgrade plan"
      open={open}
      footer={footer}
      onClose={onExit}
      contentStyles={styles.wrapper}
      isLoading={!pendingDowngrade || (!isDowngradeCycle && !newPlan)}
    >
      <div
        className="grui pt-12"
        css={[text.styles.textNormal, styles.container]}
      >
        Your downgrade from{' '}
        <strong>{isDowngradeCycle ? 'Annual' : currentPlan?.name}</strong> to{' '}
        <strong>{isDowngradeCycle ? 'Monthly' : newPlan?.name}</strong>
        {` has been `}
        <strong>
          scheduled for {strftime('%A, %Do %B ‘%y', scheduledDate * 1000)}
        </strong>.
        {planLimitsExceeded && (
          <div>
            <div className="grui mt-12">
              You are currently utilizing features from your previous plan,
              which exceeds the limits of your downgraded subscription. If these
              excess features are not removed prior to the scheduled downgrade,{' '}
              <strong className="grui tilt-highlight">
                your subscription will automatically
              </strong>
              <strong className="grui tilt-highlight">
                be upgraded back to your current plan.
              </strong>
            </div>
            <div className="grui mt-11">
              <strong css={text.styles.textSm}>
                Features outside of new plan limit:
              </strong>
              <Features
                planId={drawerResourceId}
                productType={drawerProductType}
              />
              {!!drawerOtherProductPlanId && (
                <Features
                  planId={drawerOtherProductPlanId}
                  productType={drawerOtherProductProductType}
                />
              )}
            </div>
          </div>
        )}
      </div>
      {!planLimitsExceeded && <ContactCard />}
    </AdminAccessDrawer>
  )
}

export default BillingDownGradePlanConfirmation
